import React, { useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { useDropzone } from 'react-dropzone';
import { Upload, FileSpreadsheet, CheckCircle, AlertCircle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

export const BatchUploadModal = ({ onClose, onSuccess, periodId, isAdmin = false }) => {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [partners, setPartners] = useState([]);
  const [selectedPartnerId, setSelectedPartnerId] = useState('');

  useEffect(() => {
    if (isAdmin) {
      fetchPartners();
    }
  }, [isAdmin]);

  const fetchPartners = async () => {
    try {
      const response = await axios.get(`${API}/users/partners`, getAuthHeaders());
      // Ordenar parceiros por nome em ordem alfabética
      const sortedPartners = response.data.sort((a, b) => {
        const nameA = (a.name || '').toLowerCase();
        const nameB = (b.name || '').toLowerCase();
        return nameA.localeCompare(nameB);
      });
      setPartners(sortedPartners);
    } catch (error) {
      console.error('Erro ao carregar parceiros:', error);
    }
  };

  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      setFile(acceptedFiles[0]);
      setResult(null);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls']
    },
    maxFiles: 1
  });

  const handleUpload = async () => {
    if (!file) {
      toast.error('Selecione um arquivo Excel');
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (periodId) {
        formData.append('period_id', periodId);
      }
      // Admin pode atribuir clientes a um parceiro
      if (isAdmin && selectedPartnerId && selectedPartnerId !== 'none') {
        formData.append('partner_id', selectedPartnerId);
      }

      const response = await axios.post(`${API}/clients/batch-upload`, formData, {
        ...getAuthHeaders(),
        headers: {
          ...getAuthHeaders().headers,
          'Content-Type': 'multipart/form-data'
        }
      });

      setResult(response.data);
      
      if (response.data.created > 0) {
        toast.success(`${response.data.created} cliente(s) cadastrado(s) com sucesso!`);
      }
      
      if (response.data.errors?.length > 0) {
        toast.warning(`${response.data.errors.length} erro(s) encontrado(s)`);
      }

    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao processar arquivo');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (result?.created > 0) {
      onSuccess();
    }
    onClose();
  };

  return (
    <Dialog open={true} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px] max-h-[85vh] overflow-y-auto" data-testid="batch-upload-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" data-testid="batch-upload-title">
            <FileSpreadsheet className="w-5 h-5 text-primary" />
            Upload em Lote
          </DialogTitle>
          <DialogDescription>
            Faça upload de uma planilha Excel com os dados dos clientes
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {/* Admin can select partner */}
          {isAdmin && partners.length > 0 && (
            <div className="space-y-2">
              <Label htmlFor="partner">Atribuir ao Parceiro (opcional)</Label>
              <Select 
                value={selectedPartnerId} 
                onValueChange={setSelectedPartnerId}
              >
                <SelectTrigger data-testid="batch-partner-select">
                  <SelectValue placeholder="Selecione um parceiro (ou deixe em branco)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nenhum (atribuir a mim)</SelectItem>
                  {partners.map((partner) => (
                    <SelectItem key={partner.id} value={partner.id}>
                      {partner.name} {partner.company_name ? `(${partner.company_name})` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Se não selecionar, os clientes serão atribuídos a você
              </p>
            </div>
          )}

          {/* Instructions - Collapsible */}
          <details className="bg-green-50 border border-green-200 rounded-lg">
            <summary className="p-3 cursor-pointer text-sm font-medium text-green-800 hover:bg-green-100 rounded-lg">
              📋 Ver formato da planilha
            </summary>
            <div className="p-3 pt-0 text-sm text-green-800">
              <div className="bg-card p-2 rounded border text-xs font-mono">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-1">nome</th>
                      <th className="text-left p-1">documento</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b">
                      <td className="p-1">João da Silva</td>
                      <td className="p-1">12345678900</td>
                    </tr>
                    <tr>
                      <td className="p-1">Empresa LTDA</td>
                      <td className="p-1">12345678000100</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="mt-2 text-xs">
                <strong>Detecção automática:</strong> 11 dígitos = CPF | 14 dígitos = CNPJ
              </p>
            </div>
          </details>

          {/* Dropzone */}
          <div
            {...getRootProps()}
            className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
              isDragActive ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
            }`}
            data-testid="batch-dropzone"
          >
            <input {...getInputProps()} data-testid="batch-file-input" />
            <Upload className="w-10 h-10 mx-auto mb-3 text-muted-foreground" />
            {file ? (
              <div className="flex items-center justify-center gap-2">
                <FileSpreadsheet className="w-5 h-5 text-primary" />
                <span className="font-medium">{file.name}</span>
              </div>
            ) : (
              <p className="text-muted-foreground">
                {isDragActive
                  ? 'Solte o arquivo aqui...'
                  : 'Arraste um arquivo Excel ou clique para selecionar'}
              </p>
            )}
          </div>

          {/* Results */}
          {result && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
                  <CheckCircle className="w-5 h-5 text-green-600 mx-auto mb-1" />
                  <p className="text-2xl font-bold text-green-600">{result.created}</p>
                  <p className="text-xs text-green-700">Cadastrados</p>
                </div>
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-center">
                  <AlertCircle className="w-5 h-5 text-red-600 mx-auto mb-1" />
                  <p className="text-2xl font-bold text-red-600">{result.errors?.length || 0}</p>
                  <p className="text-xs text-red-700">Erros</p>
                </div>
              </div>

              {result.errors?.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 max-h-40 overflow-y-auto">
                  <p className="text-sm font-medium text-red-700 mb-2">Erros encontrados:</p>
                  <ul className="text-xs text-red-600 space-y-1">
                    {result.errors.map((error, idx) => (
                      <li key={idx}>• {error}</li>
                    ))}
                  </ul>
                </div>
              )}

              {result.created_names?.length > 0 && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <p className="text-sm font-medium text-green-700 mb-2">Clientes cadastrados:</p>
                  <ul className="text-xs text-green-600 space-y-1">
                    {result.created_names.map((name, idx) => (
                      <li key={idx}>✓ {name}</li>
                    ))}
                    {result.created > result.created_names.length && (
                      <li className="text-muted-foreground">... e mais {result.created - result.created_names.length}</li>
                    )}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              className="flex-1"
              data-testid="batch-cancel-button"
            >
              {result ? 'Fechar' : 'Cancelar'}
            </Button>
            {!result && (
              <Button
                onClick={handleUpload}
                disabled={!file || loading}
                className="flex-1"
                data-testid="batch-upload-button"
              >
                {loading ? 'Processando...' : 'Enviar'}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BatchUploadModal;
